import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
import BaseBreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import { getQuizDetails } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { getIsGTPGoal } from '@api/selectors/constants';
import { getIsDSTFlow } from '@cont/DST/utils';
import useConstants from '@api/hooks/useConstants';
import { getIsSubscribedToOffline } from '@api/selectors/subscription';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { STORE_FLAG } from '@cont/Store/TestSeries/FullSchedule/constants';

const TYPE = {
  QUIZ: 'quiz',
  SOLUTION: 'solution',
  MODAL_SOLUTION: 'modal-solution',
  ATTEMPT: 'attempt'
};

const Breadcrumbs = ({ type, useBackClick = false, onBackClick = null }) => {
  const { query, asPath } = useRouter();
  const { quizSlug, quizUID, sessionUID, goalSlug, goalUID, source } = query;
  const isDSTFlow = getIsDSTFlow(asPath);

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const isGTP = useConstants(getIsGTPGoal(quizDetails?.goal_uid));
  const { collection } = quizDetails;
  const isOfflineSubscription = useMyInfo(
    getIsSubscribedToOffline(collection?.programme?.goal?.uid)
  );

  const isFromStore = source === STORE_FLAG;

  const tags = useMemo(() => {
    const crumbs = [
      {
        title: isOfflineSubscription ? 'Centre' : 'Plus',
        href: '/'
      }
    ];

    if (isDSTFlow) {
      crumbs.push({
        title: 'Scholarship Test',
        href: `/goal/${goalSlug}/${goalUID}/scholarship-test`
      });
      return crumbs;
    }

    if (isEmpty(quizDetails)) {
      return [];
    }

    if (isGTP) {
      crumbs.push({
        title: 'Tests',
        href: `/goal/${collection.programme?.goal?.slug}/${collection.programme?.goal?.uid}/test-series`
      });
    } else {
      crumbs.push({
        title: isFromStore ? 'Store' : 'Test series',
        href: `${collection?.programme?.goal?.permalink}/${
          isFromStore ? 'store' : 'test-series'
        }`
      });
    }

    if (!isGTP) {
      crumbs.push({
        title: quizDetails?.collection?.name,
        href: quizDetails?.collection?.programme?.relative_link
      });
    }

    if (type === TYPE.ATTEMPT || type === TYPE.MODAL_SOLUTION) {
      crumbs.push({
        title: quizDetails.title,
        href: quizDetails.relative_link
      });
    }

    if (type === TYPE.SOLUTION) {
      crumbs.push({
        title: quizDetails.title,
        href: '/quiz/[quizSlug]/[quizUID]/attempts/[sessionUID]',
        as: `/quiz/${quizSlug}/${quizUID}/attempts/${sessionUID}`
      });
    }

    return crumbs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizDetails, type]);

  return (
    <BaseBreadCrumbs
      tags={tags}
      useBackClick={useBackClick}
      onBackClick={onBackClick}
    />
  );
};

export default Breadcrumbs;

Breadcrumbs.propTypes = {
  type: PropTypes.oneOf([TYPE.QUIZ, TYPE.SOLUTION, TYPE.ATTEMPT])
};

Breadcrumbs.defaultProps = {
  type: TYPE.QUIZ
};

Breadcrumbs.TYPE = TYPE;
